import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StarIcon from "@mui/icons-material/Star";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import InsightsIcon from "@mui/icons-material/Insights";
import ComparacionMensualChart from "./reportes/components/ComparacionMensualChart";
import {
  MESES_NOMBRES,
  buildComparacionMensualChartData,
  buildTopProductosMes,
  calcGananciaMes,
  filterVentasPorMes,
  formatMesAnioLargo,
  getAniosDisponibles,
  getDiaPicoMes,
  isMesActual,
  sumTotalVentas,
} from "./reportes/utils/monthReportHelpers";

const TABLE_SCROLL_SX = { maxHeight: 300, overflow: "auto" };

function MetricCard({ titulo, valor, subtitulo, icon, color = "primary" }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {titulo}
            </Typography>
            <Typography variant="h4" fontWeight={800} color={`${color}.main`}>
              {valor}
            </Typography>
            {subtitulo && (
              <Typography variant="caption" color="text.secondary">
                {subtitulo}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: `${color}.50`,
              borderRadius: 2,
              p: 1,
              display: "flex",
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, icon, children, fullWidth = false }) {
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent>
          {title && (
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              {icon}
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
            </Stack>
          )}
          {children}
        </CardContent>
      </Card>
    </Grid>
  );
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function Reportes({ store }) {
  const hoy = new Date();
  const [mesSel, setMesSel] = useState(hoy.getMonth());
  const [anioSel, setAnioSel] = useState(hoy.getFullYear());
  const [periodo, setPeriodo] = useState({
    mes: hoy.getMonth(),
    anio: hoy.getFullYear(),
  });
  const [seccion, setSeccion] = useState(0);

  const {
    config,
    ventasDelDia,
    totalVentasDelDia,
    productosBajoStock,
    productos,
    obtenerCostoPromedioProducto,
    ventasActivas,
    detalleVentas,
    creditosTienda,
    reportesInteligentes,
  } = store;

  const alertas = reportesInteligentes?.alertas || [];
  const inventarioLento = reportesInteligentes?.inventarioLento || [];
  const quiebreTallas = reportesInteligentes?.quiebreTallas || [];
  const coloresRendimiento = reportesInteligentes?.coloresRendimiento || [];
  const clientesTop = reportesInteligentes?.clientesTop || [];

  const aniosDisponibles = useMemo(
    () => getAniosDisponibles(ventasActivas),
    [ventasActivas]
  );

  const ventasPeriodo = useMemo(
    () => filterVentasPorMes(ventasActivas, periodo.mes, periodo.anio),
    [ventasActivas, periodo]
  );

  const totalVentasPeriodo = useMemo(
    () => sumTotalVentas(ventasPeriodo),
    [ventasPeriodo]
  );

  const totalProductos = productos.length;
  const totalStock = useMemo(
    () => productos.reduce((acc, p) => acc + p.stock, 0),
    [productos]
  );

  const creditosActivosTotal = useMemo(
    () =>
      (creditosTienda || [])
        .filter((c) => c.estado === "ACTIVO")
        .reduce((acc, c) => acc + (parseFloat(c.saldo) || 0), 0),
    [creditosTienda]
  );

  const ventasRecientes = useMemo(
    () =>
      [...ventasPeriodo]
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        .slice(0, 10),
    [ventasPeriodo]
  );

  const descuentosMes = useMemo(
    () =>
      ventasPeriodo.reduce(
        (acc, v) => acc + (parseFloat(v.descuento_total) || 0),
        0
      ),
    [ventasPeriodo]
  );

  const ventasConPromoMes = useMemo(
    () =>
      ventasPeriodo.filter(
        (v) =>
          (Array.isArray(v.descuentos) && v.descuentos.length > 0) ||
          (parseFloat(v.descuento_total) || 0) > 0
      ).length,
    [ventasPeriodo]
  );

  const gananciaEstimadaMes = useMemo(
    () =>
      calcGananciaMes(
        ventasActivas,
        detalleVentas,
        periodo.mes,
        periodo.anio,
        obtenerCostoPromedioProducto
      ),
    [ventasActivas, detalleVentas, periodo, obtenerCostoPromedioProducto]
  );

  const productosMasVendidosPeriodo = useMemo(
    () =>
      buildTopProductosMes(
        ventasActivas,
        detalleVentas,
        periodo.mes,
        periodo.anio
      ),
    [ventasActivas, detalleVentas, periodo]
  );

  const top5 = productosMasVendidosPeriodo.slice(0, 5);
  const maxVendido = top5.length > 0 ? top5[0].cantidad : 1;

  const esMesActual = isMesActual(periodo.mes, periodo.anio);

  const metricaDia = useMemo(() => {
    if (esMesActual) {
      return {
        titulo: "Ventas del Día",
        valor: `${config.moneda} ${totalVentasDelDia.toFixed(2)}`,
        subtitulo: `${ventasDelDia.length} transacción(es)`,
      };
    }

    const pico = getDiaPicoMes(ventasActivas, periodo.mes, periodo.anio);
    if (pico.dia == null) {
      return {
        titulo: "Mejor Día del Mes",
        valor: `${config.moneda} 0.00`,
        subtitulo: "Sin ventas en el período",
      };
    }

    return {
      titulo: "Mejor Día del Mes",
      valor: `${config.moneda} ${pico.total.toFixed(2)}`,
      subtitulo: `Día ${pico.dia} · ${pico.count} transacción(es)`,
    };
  }, [
    esMesActual,
    config.moneda,
    totalVentasDelDia,
    ventasDelDia.length,
    ventasActivas,
    periodo,
  ]);

  const { data: chartData, series: chartSeries } = useMemo(
    () => buildComparacionMensualChartData(ventasActivas, periodo.mes, periodo.anio),
    [ventasActivas, periodo]
  );

  const handleVisualizar = () => {
    setPeriodo({ mes: mesSel, anio: anioSel });
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Reportes por período
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mostrando reporte de {formatMesAnioLargo(periodo.mes, periodo.anio)}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                label="Mes"
                value={mesSel}
                onChange={(e) => setMesSel(Number(e.target.value))}
              >
                {MESES_NOMBRES.map((nombre, idx) => (
                  <MenuItem key={nombre} value={idx}>
                    {nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Año</InputLabel>
              <Select
                label="Año"
                value={anioSel}
                onChange={(e) => setAnioSel(Number(e.target.value))}
              >
                {aniosDisponibles.map((anio) => (
                  <MenuItem key={anio} value={anio}>
                    {anio}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={handleVisualizar}
              sx={{ minWidth: 130 }}
            >
              Visualizar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo={metricaDia.titulo}
            valor={metricaDia.valor}
            subtitulo={metricaDia.subtitulo}
            icon={<TrendingUpIcon color="primary" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo="Ventas del Mes"
            valor={`${config.moneda} ${totalVentasPeriodo.toFixed(2)}`}
            subtitulo={`${ventasPeriodo.length} transacción(es)`}
            icon={<CalendarMonthIcon color="info" />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo="Ganancia Est. Mes"
            valor={`${config.moneda} ${gananciaEstimadaMes.toFixed(2)}`}
            subtitulo="Ingresos - Costos"
            icon={<AttachMoneyIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo="Productos"
            valor={totalProductos}
            subtitulo={`${totalStock} unidades en stock`}
            icon={<InventoryIcon color="secondary" />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo="Crédito Tienda"
            valor={`${config.moneda} ${creditosActivosTotal.toFixed(2)}`}
            subtitulo="Saldo activo pendiente"
            icon={<AttachMoneyIcon color="info" />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            titulo="Descuentos Mes"
            valor={`${config.moneda} ${descuentosMes.toFixed(2)}`}
            subtitulo={`${ventasConPromoMes} ventas con promo`}
            icon={<AttachMoneyIcon color="warning" />}
            color="warning"
          />
        </Grid>
      </Grid>

      <ComparacionMensualChart
        series={chartSeries}
        data={chartData}
        moneda={config.moneda}
      />

      <Paper
        variant="outlined"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={seccion}
          onChange={(_, v) => setSeccion(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48 }}
        >
          <Tab
            icon={<ShoppingBagIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Badge badgeContent={ventasPeriodo.length} color="primary" max={99}>
                <Box component="span" sx={{ pr: ventasPeriodo.length > 0 ? 2 : 0 }}>
                  Ventas
                </Box>
              </Badge>
            }
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
          />
          <Tab
            icon={<InventoryIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Badge
                badgeContent={productosBajoStock.length}
                color={productosBajoStock.length > 0 ? "warning" : "default"}
                max={99}
              >
                <Box component="span" sx={{ pr: productosBajoStock.length > 0 ? 2 : 0 }}>
                  Inventario
                </Box>
              </Badge>
            }
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
          />
          <Tab
            icon={<InsightsIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Badge badgeContent={alertas.length} color="error" max={99}>
                <Box component="span" sx={{ pr: alertas.length > 0 ? 2 : 0 }}>
                  Análisis
                </Box>
              </Badge>
            }
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      <TabPanel value={seccion} index={0}>
        <Grid container spacing={2}>
          <SectionCard
            title="Productos Más Vendidos"
            icon={<StarIcon color="warning" fontSize="small" />}
          >
            {top5.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                Sin ventas en este período
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {top5.map((p, i) => (
                  <Box key={p.id}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "55%" }}>
                        {i + 1}. {p.nombre}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {p.cantidad} uds
                        </Typography>
                        <Chip
                          label={`${config.moneda} ${p.total.toFixed(2)}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(p.cantidad / maxVendido) * 100}
                      sx={{ height: 5, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            title={`Últimas Ventas (${ventasRecientes.length})`}
            icon={<TrendingUpIcon color="primary" fontSize="small" />}
            fullWidth
          >
            {ventasRecientes.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                No hay ventas en este período
              </Typography>
            ) : (
              <TableContainer sx={TABLE_SCROLL_SX}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Productos</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ventasRecientes.map((v) => {
                      const detalles = detalleVentas.filter((d) => d.venta_id === v.id);
                      return (
                        <TableRow key={v.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {new Date(v.fecha).toLocaleDateString("es", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" noWrap title={detalles.map((d) => `${d.nombre} x${d.cantidad}`).join(", ")}>
                              {detalles.map((d) => `${d.nombre} x${d.cantidad}`).join(", ")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={v.metodo_pago} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {config.moneda} {v.total.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Grid>
      </TabPanel>

      <TabPanel value={seccion} index={1}>
        <Grid container spacing={2}>
          <SectionCard
            title={`Stock Bajo (≤ ${config.stockMinimo})`}
            icon={<WarningAmberIcon color="warning" fontSize="small" />}
          >
            {productosBajoStock.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                Stock suficiente en todos los productos
              </Typography>
            ) : (
              <TableContainer sx={TABLE_SCROLL_SX}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                      <TableCell>Producto</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell align="center">Stock</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productosBajoStock.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {p.nombre}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontFamily="monospace" fontSize={11}>
                            {p.codigo_barras}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={p.stock}
                            size="small"
                            color={p.stock <= 0 ? "error" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>

          <SectionCard title="Inventario Lento">
            <TableContainer sx={TABLE_SCROLL_SX}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                    <TableCell>Producto</TableCell>
                    <TableCell align="center">Días s/venta</TableCell>
                    <TableCell align="center">Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventarioLento.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">Sin inventario lento</TableCell>
                    </TableRow>
                  ) : (
                    inventarioLento.slice(0, 8).map((p) => (
                      <TableRow key={p.producto_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 140 }}>
                            {p.nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.talla || "UNICA"} / {p.color || "SIN COLOR"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{p.diasSinVenta}</TableCell>
                        <TableCell align="center">{p.stock}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title="Quiebre de Tallas" fullWidth>
            <TableContainer sx={TABLE_SCROLL_SX}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                    <TableCell>Producto</TableCell>
                    <TableCell align="center">Talla</TableCell>
                    <TableCell align="center">Vendidos</TableCell>
                    <TableCell align="center">Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quiebreTallas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">Sin datos suficientes</TableCell>
                    </TableRow>
                  ) : (
                    quiebreTallas.slice(0, 8).map((q, idx) => (
                      <TableRow key={`${q.producto_base}-${q.talla}-${idx}`}>
                        <TableCell>{q.producto_base}</TableCell>
                        <TableCell align="center">{q.talla}</TableCell>
                        <TableCell align="center">{q.vendidos}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={q.stock}
                            color={q.riesgo === "ALTO" ? "error" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
      </TabPanel>

      <TabPanel value={seccion} index={2}>
        <Grid container spacing={2}>
          <SectionCard title="Alertas Automáticas" fullWidth>
            {alertas.length === 0 ? (
              <Typography color="text.secondary">Sin alertas críticas por ahora</Typography>
            ) : (
              <Box sx={{ maxHeight: 220, overflow: "auto" }}>
                <Stack spacing={1}>
                  {alertas.slice(0, 8).map((a, idx) => (
                    <Alert
                      key={`${a.tipo}-${idx}`}
                      severity={a.prioridad === "ALTA" ? "warning" : "info"}
                      sx={{ py: 0.25 }}
                    >
                      {a.mensaje}
                    </Alert>
                  ))}
                </Stack>
              </Box>
            )}
          </SectionCard>

          <SectionCard title="Colores que Mejor Funcionan">
            <TableContainer sx={TABLE_SCROLL_SX}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                    <TableCell>Color</TableCell>
                    <TableCell align="center">Unidades</TableCell>
                    <TableCell align="right">Margen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coloresRendimiento.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">Sin datos</TableCell>
                    </TableRow>
                  ) : (
                    coloresRendimiento.slice(0, 8).map((c) => (
                      <TableRow key={c.color}>
                        <TableCell>{c.color}</TableCell>
                        <TableCell align="center">{c.unidades}</TableCell>
                        <TableCell align="right">
                          {config.moneda} {c.margen.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title="Clientes Top">
            <TableContainer sx={TABLE_SCROLL_SX}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="center">Compras</TableCell>
                    <TableCell align="right">LTV</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesTop.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">Sin clientes suficientes</TableCell>
                    </TableRow>
                  ) : (
                    clientesTop.slice(0, 8).map((c) => (
                      <TableRow key={c.cliente_id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {c.nombre}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{c.compras}</TableCell>
                        <TableCell align="right">
                          {config.moneda} {(c.monto || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
      </TabPanel>
    </Box>
  );
}
